import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api';
import { Check, Smile, AlertTriangle, X } from 'lucide-react';

const AddFaceModal = ({ device, person, onClose }) => {
    const [step, setStep] = useState('initiating'); // initiating | scanning | success | error
    const [errorMessage, setErrorMessage] = useState('');
    
    const pollingIntervalId = useRef(null);
    const timeoutId = useRef(null);

    // 1. Start the Process Automatically on Mount
    useEffect(() => {
        startFaceEnrollment();
        
        // Cleanup on unmount
        return () => stopPolling();
    }, []);

    const stopPolling = () => {
        if (pollingIntervalId.current) clearInterval(pollingIntervalId.current);
        if (timeoutId.current) clearTimeout(timeoutId.current);
    };

    const startFaceEnrollment = async () => {
        setStep('initiating');
        setErrorMessage('');
        
        try {
            // A. Tell device to enter Face Enroll Mode
            await api.initiateFaceEnrollment(device.id, { userId: person.employee_code });
            setStep('scanning');
            startPolling();
        } catch (error) {
            console.error("Face Enroll Init Error:", error);
            if (error.message && error.message.includes('MenuProcessing')) {
                setErrorMessage('The device menu is open. Please close it and try again.');
            } else {
                setErrorMessage(error.message || 'Failed to start face scanner.');
            }
            setStep('error');
        }
    };

    const startPolling = useCallback(() => {
        stopPolling();

        // Timeout after 60 seconds
        timeoutId.current = setTimeout(() => {
            stopPolling();
            setStep('error');
            setErrorMessage("Enrollment timed out. User did not scan their face in time.");
        }, 60000); 

        // Poll every 2 seconds
        pollingIntervalId.current = setInterval(async () => {
            try {
                // We reuse checkAddFingerStatus because it checks for BOTH Finger OR Face events in the DB
                const response = await api.checkAddFingerStatus(device.id, person.employee_code);
                
                if (response.status === 'finger_added') { // The API returns this status for any biometric add
                    stopPolling();
                    toast.success('Face enrolled successfully!');
                    setStep('success');
                    
                    // Auto close after success
                    setTimeout(() => {
                        onClose();
                    }, 2000);
                }
            } catch (error) {
                console.warn("Polling error:", error);
                // Don't stop polling on network blips, only on timeout
            }
        }, 2000); 
    }, [device.id, person.employee_code, onClose]);

    // --- RENDER HELPERS ---
    
    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '400px' }}>
                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 className="modal-title">Enroll Face</h2>
                    <button onClick={onClose} className="icon-button"><X size={20} /></button>
                </div>
                
                <div className="modal-body" style={{ textAlign: 'center', padding: '20px 0' }}>
                    
                    {step === 'initiating' && (
                        <div>
                            <div className="spinner" style={{ margin: '0 auto 20px auto' }}></div>
                            <p>Connecting to device...</p>
                        </div>
                    )}

                    {step === 'scanning' && (
                        <div>
                            <div style={{ 
                                width: '100px', height: '100px', background: '#eff6ff', 
                                borderRadius: '50%', margin: '0 auto 20px auto',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#3b82f6'
                            }}>
                                <Smile size={48} className="pulse-animation" /> 
                            </div>
                            <h3 className="text-lg font-bold mb-2">Look at the Camera</h3>
                            <p className="text-gray-600 mb-4">
                                Please ask <strong>{person.first_name}</strong> to stand in front of the device.
                            </p>
                            <p className="text-xs text-gray-400">Device: {device.name}</p>
                        </div>
                    )}

                    {step === 'success' && (
                        <div>
                            <div style={{ background: '#dcfce7', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto', color: '#16a34a' }}>
                                <Check size={40} />
                            </div>
                            <h3 className="text-xl font-bold text-green-600 mb-2">Success!</h3>
                            <p>Face data saved.</p>
                        </div>
                    )}

                    {step === 'error' && (
                        <div>
                            <div style={{ background: '#fee2e2', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto', color: '#dc2626' }}>
                                <AlertTriangle size={40} />
                            </div>
                            <h3 className="text-lg font-bold text-red-600 mb-2">Failed</h3>
                            <p className="text-gray-600 mb-6">{errorMessage}</p>
                            <button onClick={startFaceEnrollment} className="button action-button">
                                Try Again
                            </button>
                        </div>
                    )}

                </div>
            </div>
            
            <style jsx>{`
                .pulse-animation {
                    animation: pulse 2s infinite;
                }
                @keyframes pulse {
                    0% { transform: scale(0.95); opacity: 0.8; }
                    50% { transform: scale(1.1); opacity: 1; }
                    100% { transform: scale(0.95); opacity: 0.8; }
                }
            `}</style>
        </div>
    );
};

export default AddFaceModal;